import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { useAuth } from '../../../contexts/AuthContext';
import {
  useBotStores,
  useDeleteIntegrationConfig,
  useIntegrationConfig,
  useIntegrations,
  useToggleIntegrationConfig,
} from '../../../hooks/useIntegrations';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SoftChip from '../../../components/dashboard/SoftChip';
import { formatDateTime } from '../../../utils/formatters';
import DealerspaceAuthorizationCard from './DealerspaceAuthorizationCard';
import IntegrationLogCard from './IntegrationLogCard';
import IntegrationLogo from './IntegrationLogo';
import StoreChips from './StoreChips';
import { customerConfig, integrationStatus, slotTakenBy, statusMeta } from './integrationStatus';
import { PROVIDERS, displayValue, fieldLabelKey, settingValue } from './providers';

const ACTION_ERROR_KEYS = { 403: 'integrations.form.errors.forbidden' };

// "08:00:00" → "08:00".
const hhmm = (time) => time.slice(0, 5);

export default function IntegrationDetail() {
  const { type, provider } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { collaborator, customer } = useAuth();
  const [actionError, setActionError] = useState('');
  const spec = PROVIDERS[provider];
  const detailPath = `/integrations/${type}/${provider}`;

  const integrationsQuery = useIntegrations();
  const integration = integrationsQuery.data?.find((item) => item.type === type && item.key === provider);
  const summary = customerConfig(integration);
  const configQuery = useIntegrationConfig(type, summary?.id);
  const config = configQuery.data;
  const storesQuery = useBotStores(type === 'stock' ? customer?.uuid : undefined);

  const showActionError = (error) =>
    setActionError(t(ACTION_ERROR_KEYS[error?.response?.status] ?? 'integrations.actionError'));

  const toggle = useToggleIntegrationConfig(type, summary?.id, {
    onSuccess: () => setActionError(''),
    onError: showActionError,
  });

  // Deletes straight away and lets the list reopen the undo snackbar via
  // `seedDelete`: the backend soft-deletes, so "Desfazer" restores this row.
  const remove = useDeleteIntegrationConfig(type, summary?.id, {
    onSuccess: () =>
      navigate('/integrations', {
        state: { seedDelete: { ids: [{ type, id: summary.id }], message: t('integrations.deleteSuccess', { name: integration.name }) } },
      }),
    onError: showActionError,
  });

  const name = integration?.name ?? t('integrations.detailTitle');
  const header = (
    <PageHeader
      icon={integration && <IntegrationLogo integration={integration} size={32} />}
      title={name}
      breadcrumbs={[
        { label: t('integrations.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('integrations.breadcrumb.list'), to: '/integrations' },
        { label: name },
      ]}
    />
  );

  const renderBody = () => {
    if (integrationsQuery.isLoading || configQuery.isLoading) {
      return <LoadingState message={t('integrations.states.loading')} />;
    }
    const failed = [integrationsQuery, configQuery].find((query) => query.isError);
    if (failed) {
      const status = failed.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('integrations.states.permissionDeniedTitle')}
            message={t('integrations.states.permissionDeniedMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('integrations.states.errorTitle')}
          message={t('integrations.states.errorMessage')}
          actionLabel={t('integrations.states.retry')}
          onRetry={failed.refetch}
        />
      );
    }
    if (!integration || !spec) {
      return <ErrorState title={t('integrations.states.notFoundTitle')} message={t('integrations.states.notFoundMessage')} />;
    }
    if (!summary) {
      const takenBy = slotTakenBy(integrationsQuery.data, integration);
      return (
        <ErrorState
          title={t('integrations.states.notConfiguredTitle', { name })}
          message={
            takenBy
              ? t('integrations.form.slotTaken', { name: takenBy.name, type: t(`integrations.types.${type}`) })
              : t('integrations.states.notConfiguredMessage')
          }
          actionLabel={takenBy ? undefined : t('integrations.actions.configure')}
          onRetry={takenBy ? undefined : () => navigate(`${detailPath}/new`)}
        />
      );
    }

    const status = integrationStatus(integration);
    const bot = storesQuery.data?.find((item) => item.botId === config.bot_id);
    const orphanBot = storesQuery.isSuccess ? t('integrations.values.orphanBot', { id: config.bot_id }) : config.bot_id;
    const rows = [
      { label: t('integrations.fields.api_url'), value: config.api_url },
      ...(type === 'crm'
        ? ['export_on_lead_created', 'export_on_lead_expired'].map((field) => ({
          label: t(`integrations.fields.${field}`),
          value: t(config[field] ? 'integrations.values.yes' : 'integrations.values.no'),
        }))
        : [
          {
            label: t('integrations.fields.bot_id'),
            value: config.bot_id && (bot ? <StoreChips stores={bot.stores} /> : orphanBot),
          },
          {
            label: t('integrations.fields.syncWindow'),
            value: config.sync_window_start
              ? t('integrations.values.window', { start: hhmm(config.sync_window_start), end: hhmm(config.sync_window_end) })
              : t('integrations.values.anyTime'),
          },
        ]),
      // Write-only: the API never returns them.
      { label: t('integrations.fields.credentials'), value: t('integrations.values.credentialsStored') },
      ...spec.settings.map((field) => ({
        label: t(fieldLabelKey(provider, field.name)),
        value: displayValue(provider, field, settingValue(config, field), t),
      })),
      { label: t('integrations.fields.updatedAt'), value: formatDateTime(config.updated_at, i18n.language) },
    ];

    return (
      <Stack spacing={3} width="100%" pb={3}>
        <Card>
          <CardHeader
            avatar={<IntegrationLogo integration={integration} />}
            title={integration.name}
            subheader={t(`integrations.types.${type}`)}
            titleTypographyProps={{ variant: 'h6' }}
            action={<SoftChip sx={{ mt: 1 }} color={statusMeta[status].color} label={t(`integrations.statuses.${status}`)} />}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              {rows.map((row, index) => (
                <ListItem key={row.label} disableGutters divider={index < rows.length - 1}>
                  <ListItemText
                    primary={row.label}
                    secondary={row.value ?? t('notAvailable')}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    secondaryTypographyProps={{ component: 'div', variant: 'body1', color: 'text.primary', sx: { overflowWrap: 'anywhere' } }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          {actionError && (
            <CardContent>
              <Alert severity="error" onClose={() => setActionError('')}>
                {actionError}
              </Alert>
            </CardContent>
          )}
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {t('integrations.actions.delete')}
            </Button>
            <Button
              variant="outlined"
              startIcon={config.enabled ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
              onClick={() => toggle.mutate(!config.enabled)}
              disabled={toggle.isPending}
            >
              {t(config.enabled ? 'integrations.actions.disable' : 'integrations.actions.enable')}
            </Button>
            <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`${detailPath}/edit`)}>
              {t('integrations.actions.edit')}
            </Button>
          </CardActions>
        </Card>

        {provider === 'dealerspace' && summary.authorization && (
          <DealerspaceAuthorizationCard
            configId={summary.id}
            authorization={summary.authorization}
            isInternal={Boolean(collaborator?.is_internal)}
          />
        )}

        <IntegrationLogCard type={type} configId={summary.id} />
      </Stack>
    );
  };

  return <BoundedPageLayout header={header}>{renderBody()}</BoundedPageLayout>;
}
